import { PlateEditor } from "@/components/editor/plate-editor.tsx";
import { useToast } from '@/components/ui/use-toast';
import { clearAutoSavedContentAfterSave, restoreAutoSavedContent, useAutoSave } from '@/hooks/useAutoSave';
import { ChaptersOverview } from "@/pages/chapters/components/chapters-overview";
import { plotcardEntityKeys } from '@/services/query/plotcard-entities-queries';
import { getChapterById, getChapterContent, saveChapterContent } from '@/services/supabase/chapters';
import { useQueryClient } from '@tanstack/react-query';
import { Value } from '@udecode/plate';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useChapterBeats } from "@/hooks/useChapterBeats";

export default function ChapterPage() {
    const { chapterId, storyId, projectId } = useParams();
    const { toast } = useToast();
    const [content, setContent] = useState<Value>([{ type: 'p', children: [{ text: '' }] }]);
    const [chapterMetadata, setChapterMetadata] = useState<{ title: string, description?: string, storyId?: string } | null>(null);

    const [loading, setLoading] = useState(false);
    const queryClient = useQueryClient();

    // Fetch chapter beats
    const { data: chapterBeatsData } = useChapterBeats(chapterId);

    // Map beats for AI context
    const mappedBeats = useMemo(() => {
        if (!chapterBeatsData) return [];
        return chapterBeatsData.map(beat => ({
            id: beat.id,
            displayOrder: beat.display_order,
            name: beat.title,
            description: beat.description
        }));
    }, [chapterBeatsData]);

    // Auto-save functionality
    const autoSave = useAutoSave({
        content,
        id: chapterId || '',
        type: 'chapter',
        enabled: !!chapterId,
        onAutoSave: (data) => {
            console.log('Chapter content auto-saved:', data.timestamp);
        }
    });

    // Memoize the loadChapterContent function to prevent recreating it on every render
    const loadChapterContent = useCallback(async () => {
        if (!chapterId) {
            console.log('No chapterId provided, skipping content load');
            return;
        }

        try {
            console.log(`Starting to load content for chapter ID ${chapterId}`);
            setLoading(true);

            // Fetch chapter metadata in parallel with content
            const [contentData, chapterData] = await Promise.all([
                getChapterContent(parseInt(chapterId)),
                getChapterById(parseInt(chapterId)).catch(e => {
                    console.error('Failed to fetch chapter metadata:', e);
                    return null;
                })
            ]);

            if (chapterData) {
                setChapterMetadata({
                    title: chapterData.title,
                    description: chapterData.description,
                    storyId: chapterData.story_id.toString()
                });
            }

            let finalContent;
            if (contentData && contentData.content) {
                console.log(`Chapter content loaded for chapter ID ${chapterId}`);
                finalContent = contentData.content;
            } else {
                console.log(`No content found for chapter ID ${chapterId}, using default`);
                // If no content exists yet, use the default empty paragraph
                finalContent = [{ type: 'p', children: [{ text: '' }] }];
            }

            // Check for auto-saved content
            const autoSavedData = restoreAutoSavedContent(chapterId, 'chapter');
            if (autoSavedData) {
                // Compare timestamps to determine which content is newer
                const dbTimestamp = contentData?.updated_at ? new Date(contentData.updated_at).getTime() : 0;
                const autoSaveTimestamp = new Date(autoSavedData.timestamp).getTime();

                if (autoSaveTimestamp > dbTimestamp) {
                    console.log('Using auto-saved content (newer than database)');
                    finalContent = autoSavedData.content;
                } else {
                    console.log('Database content is newer, clearing auto-saved content');
                    clearAutoSavedContentAfterSave(chapterId, 'chapter');
                }
            }

            setContent(finalContent);
        } catch (error) {
            console.error(`Failed to load chapter content for chapter ID ${chapterId}:`, error);
            toast({
                title: 'Error',
                description: 'Failed to load chapter content. Please check your permissions.',
                variant: 'destructive',
            });
            // Set default content even on error to prevent empty editor
            setContent([{ type: 'p', children: [{ text: '' }] }]);
        } finally {
            setLoading(false);
        }
    }, [chapterId, toast]);

    useEffect(() => {
        if (chapterId) {
            // Load content when chapterId changes
            loadChapterContent();

            // Log chapter change to help with debugging
            console.log(`ChapterPage: Switched to chapter ID ${chapterId}`);

            // Force invalidation of any cached data for this chapter
            // This helps ensure sidebar items are properly refreshed
            queryClient.invalidateQueries({
                queryKey: plotcardEntityKeys.byChapter(chapterId)
            });
        }
    }, [chapterId, loadChapterContent, queryClient]);

    // Handle content change - memoized to prevent recreation on every render
    const handleContentChange = useCallback((newContent: Value) => {
        setContent(newContent);
    }, []);

    // Handle save operation for the chapter
    const handleSaveChapter = useCallback(async (chapterContent: Value) => {
        if (!chapterId) {
            console.error('No chapter ID');
            return;
        }

        try {
            console.log('Saving chapter content...');

            // Save content to the database
            await saveChapterContent(parseInt(chapterId), chapterContent);

            // Clear auto-saved content after successful save
            clearAutoSavedContentAfterSave(chapterId, 'chapter');

            toast({
                title: 'Chapter Saved',
                description: 'Your chapter has been saved successfully.',
                duration: 3000,
            });
        } catch (error) {
            console.error('Error saving chapter content:', error);
            toast({
                title: 'Error',
                description: 'Failed to save your chapter. Please try again.',
                variant: 'destructive',
            });
        }
    }, [chapterId, toast]);

    // Memoize the editor component to prevent unnecessary re-renders
    const editorComponent = useMemo(() => {

        if (loading) {
            return (
                <div className="flex items-center justify-center w-full h-full">
                    <p>Loading chapter content...</p>
                </div>
            );
        }

        return (
            <PlateEditor
                content={content}
                onChange={handleContentChange}
                chapterId={chapterId}
                storyId={storyId || chapterMetadata?.storyId}
                projectId={projectId}
                chapterTitle={chapterMetadata?.title}
                chapterDescription={chapterMetadata?.description}
                chapterBeats={mappedBeats}
                showSaveButton={true}
                onSave={handleSaveChapter}
                autoSaveData={autoSave}
            />
        );
    }, [loading, content, handleContentChange, chapterId, storyId, projectId, handleSaveChapter, chapterMetadata, autoSave, mappedBeats]);

    return (
        <>
            {chapterId ? (
                <div className="h-full w-full overflow-hidden">
                    {editorComponent}
                </div>
            ) : (
                <div className="h-full w-full flex flex-col">
                    <ChaptersOverview />
                </div>
            )}
        </>
    );
}
